import { useState } from "react";
import Draggable from "react-draggable";
import { Box, IconButton, makeStyles } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import { CANVAS_WIDTH, CANVAS_HEIGHT, STATUS_COLOR_STRONG, getTableLayout } from "../canvasConstants";

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    '&:hover $hoverMenu': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  hoverMenu: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
    borderRadius: theme.shape.borderRadius,
    padding: '2px',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.2s ease, visibility 0.2s ease',
    zIndex: 10,
  },
  reference: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    pointerEvents: 'none',
    userSelect: 'none',
  },
}));

// tabletop and chairs drawn from the shared layout, so the edit window matches the other plans
export const TableShape = ({ shape, persons, status }) => {
  const layout = getTableLayout(shape, persons);
  const { top } = layout;
  const fill = STATUS_COLOR_STRONG[status] ?? STATUS_COLOR_STRONG.available;
  return (
    <svg width={layout.w} height={layout.h} style={{ display: 'block', overflow: 'visible' }}>
      {layout.chairs.map((c, i) => (
        <rect
          key={i}
          x={-c.w / 2}
          y={-c.h / 2}
          width={c.w}
          height={c.h}
          rx={3}
          fill={fill}
          opacity={0.55}
          transform={`translate(${c.cx} ${c.cy}) rotate(${c.rotation})`}
        />
      ))}
      {top.circle ? (
        <circle cx={top.x + top.w / 2} cy={top.y + top.h / 2} r={top.w / 2} fill={fill} stroke="rgba(0,0,0,0.25)" strokeWidth={1.5} />
      ) : (
        <rect x={top.x} y={top.y} width={top.w} height={top.h} rx={6} fill={fill} stroke="rgba(0,0,0,0.25)" strokeWidth={1.5} />
      )}
    </svg>
  );
};

export const TableIcon = ({
  shape,
  id,
  x,
  y,
  status,
  persons,
  reference,
  enableDrag,
  onDrag,
  onDragStop,
  onClick,
  onEdit,
  onDelete,
  onCheckboxToggle,
  onHover,
  labelRef,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const classes = useStyles();
  const layout = getTableLayout(shape, persons);
  const dim = { w: layout.w, h: layout.h };
  const { top } = layout;

  const handleDrag = (e, data) => {
    if (onDrag) onDrag(id, (data.x + dim.w / 2) / CANVAS_WIDTH, (data.y + dim.h / 2) / CANVAS_HEIGHT);
  };

  const handleStop = (e, data) => {
    setIsDragging(false);
    if (onDragStop) onDragStop(id, (data.x + dim.w / 2) / CANVAS_WIDTH, (data.y + dim.h / 2) / CANVAS_HEIGHT);
  };

  const handleClick = () => {
    if (onCheckboxToggle) onCheckboxToggle(id);
    if (onClick) onClick();
  };

  return (
    <Draggable
      defaultPosition={{ x: x * CANVAS_WIDTH - dim.w / 2, y: y * CANVAS_HEIGHT - dim.h / 2 }}
      position={null}
      bounds="parent"
      disabled={!enableDrag}
      onStart={() => setIsDragging(true)}
      onDrag={handleDrag}
      onStop={handleStop}
    >
      <div
        className={classes.root}
        onClick={handleClick}
        onMouseEnter={() => (onHover ? onHover() : null)}
        style={{
          width: dim.w,
          height: dim.h,
          cursor: isDragging ? 'grabbing' : enableDrag ? 'grab' : 'pointer',
        }}
      >
        <TableShape shape={shape} persons={persons} status={status} />
        <span
          ref={labelRef}
          className={classes.reference}
          style={{ left: top.x + 2, top: top.y, width: top.w - 4, height: top.h }}
        >
          {reference}
        </span>

        {enableDrag && (
          <Box className={classes.hoverMenu}>
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => { e.stopPropagation(); if (onEdit) onEdit(id); }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="secondary"
              onClick={(e) => { e.stopPropagation(); if (onDelete) onDelete(id); }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        )}
      </div>
    </Draggable>
  );
};
